import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Fade,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import ShieldOutlined from '@mui/icons-material/ShieldOutlined';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import ArrowForward from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';

interface KYCVerificationModalProps {
  open: boolean;
  onClose: () => void;
}

const KYCVerificationModal: React.FC<KYCVerificationModalProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation('kycVerificationModal');
  const theme = useTheme();
  const navigate = useNavigate();

  const handleKycClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose();
    navigate('/profile?tab=kyc');
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="kyc-verification-modal"
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 400 }}
      PaperProps={{
        sx: {
          maxWidth: { xs: '90%', sm: '440px' },
          width: '100%',
          bgcolor: theme.palette.background.paper,
          borderRadius: '16px',
          backgroundImage: 'none',
        },
      }}
    >
      <IconButton
        onClick={handleClose}
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          zIndex: 20,
          color: theme.palette.text.secondary,
        }}
        aria-label="Close"
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ p: { xs: 4, sm: 6 } }}>
        <Stack spacing={3} alignItems="center">
          <Box sx={{ position: 'relative', mb: 2 }}>
            <ShieldOutlined sx={{ fontSize: 64, color: theme.palette.primary.main }} />
            <WarningAmberOutlined
              sx={{
                position: 'absolute',
                top: 0,
                right: -10,
                fontSize: 32,
                color: theme.palette.warning.main,
                bgcolor: theme.palette.background.paper,
                borderRadius: '50%',
                p: 0.5,
              }}
            />
          </Box>

          <Typography
            variant="h5"
            sx={{
              fontWeight: 'bold',
              textAlign: 'center',
            }}
          >
            {t('VERIFICATION_REQUIRED')}
          </Typography>

          <Box
            sx={{
              width: 64,
              height: 4,
              background: theme.palette.primary.main,
              borderRadius: '9999px',
            }}
          />

          <Typography
            sx={{
              color: theme.palette.text.secondary,
              textAlign: 'center',
            }}
          >
            {t('EXPLANATORY_TEXT')}
          </Typography>

          <Button
            onClick={handleKycClick}
            variant="contained"
            fullWidth
            sx={{
              py: 1.5,
              px: 3,
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '16px',
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="center"
            >
              <span>{t('BUTTON_COMPLETE_KYC')}</span>
              <ArrowForward />
            </Stack>
          </Button>

          <Typography
            sx={{
              color: theme.palette.text.secondary,
              fontSize: '0.875rem',
              textAlign: 'center',
            }}
          >
            {t('SUBTEXT')}
          </Typography>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default KYCVerificationModal;
